// src/pages/StripeConnectionSuccess.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button/Button';
import Typography from '../components/Typography';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../theme';
import CheckCircleIcon from '../components/Icons/CheckCircleIcon';
import InfoCircleIcon from '../components/Icons/InfoCircleIcon';
import ArrowRightUpIcon from '../components/Icons/ArrowRightUpIcon';

function StripeConnectionSuccess() {
  const { t } = useTranslation('stripeManagement');
  const navigate = useNavigate();
  const { user, stripeStatus, refreshStripeStatus } = useAuth();

  // Al montar la pantalla, forzar la verificación real
  useEffect(() => {
    if (user) {
      refreshStripeStatus(user.id);
    }
  }, [user, refreshStripeStatus]);

  const handleGoToSettings = () => {
    if (user) {
      navigate(`/user/${user.id}/settings`);
    } else {
      navigate('/discover');
    }
  };

  // const handleCreateEvent = () => {
  //   navigate('/create-event');
  // };

  const handleOpenStripeDashboard = () => {
    window.open('https://dashboard.stripe.com/', '_blank');
  };

  return (
    <StripeConnectionSection>
      <Container>
        <Typography as="h2" $variant="title-3-semibold" $align="center">
          {t('connectionSuccess.title')}
          {/* ¡Volviste de Stripe! */}
        </Typography>

        {/* Mensaje condicional según chargesEnabled */}
        {stripeStatus.loading ? (
          <LoadingBanner>
            <Typography as="p" color={theme.colors.defaultWeak}>
              {/* Verificando tu cuenta de Stripe... */}
              {t('connectionSuccess.loading')}
            </Typography>
          </LoadingBanner>
        ) : stripeStatus.chargesEnabled ? (
          <ContentWrapper>
            <StatusBanner>
              {/* <CheckCircleIcon fill={theme.colors.successMain} /> */}
              <BannerHeader>
                <InfoCircleIcon fill={theme.colors.defaultStrong} />
                <Typography as="p" $variant="title-5-semibold" $align="center" color={theme.colors.defaultStrong}>
                  {t('connectionSuccess.verificationsRequiredTitle')}
                </Typography>
              </BannerHeader>

              {/* <Typography as="p" $variant="body-1-semibold" $align="center" color={theme.colors.defaultStrong}>
                {t('settings.payments.stripe.connectionSuccess.chargesEnabled')}
              </Typography> */}
              <Typography as="p" $variant="body-1-medium" $align="center" color={theme.colors.defaultWeak}>
                {t('connectionSuccess.verificationsRequiredSubtitle')}
              </Typography>
            </StatusBanner>
            <Actions>
              <Button onClick={handleGoToSettings}>
                {t('connectionSuccess.goToSettings')}
                {/* Volver a Configuración */}
              </Button>
              {/* <Button $variant="outline" onClick={handleCreateEvent}>
                {t('settings.payments.stripe.connectionSuccess.createEvent')}
              </Button> */}
              <Button $variant="outline" onClick={handleOpenStripeDashboard}>
                {t('connectionSuccess.goToDashboard')}
                {/* Ir al Dashboard de Stripe */}
                <ArrowRightUpIcon fill={theme.colors.defaultStrong} />

              </Button>
            </Actions>
          </ContentWrapper>
        ) : (
          <ContentWrapper>
            <StatusBanner>
              <InfoCircleIcon fill={theme.colors.defaultWeak} />
              <Typography as="p" $align="center" color={theme.colors.defaultStrong}>
                {t('connectionSuccess.chargesDisabled')}
                {/* Parece que aún no está habilitado para cobros (revisa tu configuración). */}
              </Typography>
            </StatusBanner>
            <Actions>
              <Button onClick={handleGoToSettings}>
                {t('connectionSuccess.goToSettings')}
                {/* Volver a Configuración */}
              </Button>
              <Button $variant="outline" onClick={handleOpenStripeDashboard}>
                {t('connectionSuccess.goToDashboard')}
                <ArrowRightUpIcon fill={theme.colors.defaultWeak} />
                {/* Ir al Dashboard de Stripe */}
              </Button>
            </Actions>
          </ContentWrapper>
        )}
      </Container>
    </StripeConnectionSection>
  );
}

export default StripeConnectionSuccess;

const StripeConnectionSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top:  ${({ theme }) => theme.sizing.md};
`;

const Container = styled.div`
  max-width: 1440px;
  // margin: 40px auto;
  // text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: ${({ theme }) => theme.sizing.md};
  padding-right: ${({ theme }) => theme.sizing.md};
  gap: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => theme.sizing.sm};
    padding-right: ${({ theme }) => theme.sizing.sm};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  max-width: 400px
`;

const StatusBanner = styled.div`
  // margin-top: ${({ theme }) => theme.sizing.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizing.xs};
  // padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm};
  // max-width: 320px;
`;

const SuccessBanner = styled(StatusBanner)`
  background-color: ${({ theme }) => theme.fill.successAlphaMain16};
  border: 1px solid ${({ theme }) => theme.border.successMain};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizing.xs};
  max-width: 320px;
`;

const LoadingBanner = styled(StatusBanner)`
  border: 1px transparent;
  background-color: transparent;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.sizing.sm};
  flex-wrap: wrap;
`;

const BannerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;
